// REST client for the spezzial-ai-wrap plugin. Same-origin (the widget runs on
// the WP page), authenticated by the logged-in cookie + an X-WP-Nonce header —
// no JWT. Endpoints + nonce come from the widget config (data-* attributes).
import type { WidgetConfig } from './config';

export type QuotaSnapshot = {
  used: number;
  limit: number;
  remaining: number;
  period: string;
  resets_on: string;
};

export type ColorizeArgs = {
  vehicleImage: string; // data-URL
  vehicleMime: string;
  productId: number;
  productImageId: number;
  productImageUrl: string;
  productName: string;
};

export type ColorizeResult = {
  status: number;
  image?: string;
  quota?: QuotaSnapshot;
  error?: string;
  message?: string;
};

function headers(cfg: WidgetConfig, json = false): HeadersInit {
  const h: Record<string, string> = { 'X-WP-Nonce': cfg.nonce };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/** Log in via the plugin's AJAX endpoint (sets the WP auth cookie). */
export async function login(
  cfg: WidgetConfig,
  username: string,
  password: string,
): Promise<{ ok: boolean; message?: string }> {
  try {
    const res = await fetch(cfg.restLogin, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ username, password, nonce: cfg.loginNonce }),
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
    return { ok: res.ok && data.ok === true, message: data.message };
  } catch {
    return { ok: false };
  }
}

/** Register a new customer via the plugin's AJAX endpoint (sets the WP auth cookie). */
export async function register(
  cfg: WidgetConfig,
  email: string,
  password: string,
  honeypot: string,
  marketingOptIn: boolean,
): Promise<{ ok: boolean; message?: string }> {
  try {
    const res = await fetch(cfg.restRegister, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        email,
        password,
        hp: honeypot,
        nonce: cfg.registerNonce,
        marketing_optin: marketingOptIn,
      }),
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
    return { ok: res.ok && data.ok === true, message: data.message };
  } catch {
    return { ok: false };
  }
}

/** Current usage snapshot; null when unauthorized (401) or unreachable. */
export async function fetchQuota(cfg: WidgetConfig): Promise<QuotaSnapshot | null> {
  try {
    const res = await fetch(cfg.restQuota, {
      headers: headers(cfg),
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    return (await res.json()) as QuotaSnapshot;
  } catch {
    return null;
  }
}

/** Request a preview; returns the raw status so the UI can handle 401/429/502. */
export async function colorize(cfg: WidgetConfig, args: ColorizeArgs): Promise<ColorizeResult> {
  const res = await fetch(cfg.restColorize, {
    method: 'POST',
    headers: headers(cfg, true),
    credentials: 'same-origin',
    body: JSON.stringify({
      vehicle_image: args.vehicleImage,
      vehicle_mime: args.vehicleMime,
      product_id: args.productId,
      product_image_id: args.productImageId,
      product_image_url: args.productImageUrl,
      product_name: args.productName,
    }),
  });
  const data = (await res.json().catch(() => ({}))) as Omit<ColorizeResult, 'status'>;
  return { status: res.status, ...data };
}
